/**
 * Structured data (JSON-LD) for public place detail pages.
 * Maps Prisma/GraphQL `PlaceCategory` to schema.org types for richer search signals.
 */

import { env } from '@web/src/lib/env.client'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import {
  geoPhraseForLocationDescription,
  plainTextFromRichContent,
  sameAsFromWebLinks as relatedLinkFromWebLinks,
  SEO_META_DESCRIPTION_MAX_CHARS,
  truncateMetaDescription,
} from '@web/src/lib/seo/seo-common'
import { absoluteUrlForPath, baseUrl } from '@web/src/lib/utilsClient'
import type { PlaceCategoryEnum, PlaceQuery, ReviewsQuery } from 'types/graphql'

type PlaceForJsonLd = NonNullable<PlaceQuery['place']>
type ReviewsConnection = ReviewsQuery['reviews']

/** Primary schema.org @type for each place category (aligned with https://schema.org). */
function schemaOrgTypeForPlaceCategory(category: PlaceCategoryEnum): string {
  switch (category) {
    case 'NUDE_BEACH':
      return 'Beach'
    case 'RESORT':
      return 'Resort'
    case 'CLUB':
      return 'EntertainmentBusiness'
    case 'CAMPING':
      return 'Campground'
    case 'OTHER':
      return 'LocalBusiness'
    default: {
      const _exhaustive: never = category
      return _exhaustive
    }
  }
}

/** Human-readable category for schema.org `category` (English; common practice for structured data). */
function schemaCategoryLabel(category: PlaceCategoryEnum): string {
  switch (category) {
    case 'NUDE_BEACH':
      return 'Nude beach'
    case 'RESORT':
      return 'Resort'
    case 'CLUB':
      return 'Club'
    case 'CAMPING':
      return 'Campground'
    case 'OTHER':
      return 'Lifestyle venue'
    default: {
      const _exhaustive: never = category
      return _exhaustive
    }
  }
}

function categoryPhraseForSentence(category: PlaceCategoryEnum): string {
  const label = schemaCategoryLabel(category)
  return label.charAt(0).toLowerCase() + label.slice(1)
}

/**
 * Short category phrase for the HTML `<title>` (English; common SEO practice).
 * Uses search-friendly modifiers where they help without duplicating the plain UI label alone.
 */
function categorySegmentForSeoTitle(category: PlaceCategoryEnum): string {
  switch (category) {
    case 'NUDE_BEACH':
      return 'Nude Beach'
    case 'RESORT':
      return 'Lifestyle Resort'
    case 'CLUB':
      return 'Lifestyle Club'
    case 'CAMPING':
      return 'Lifestyle Campground'
    case 'OTHER':
      return 'Lifestyle Venue'
    default: {
      const _exhaustive: never = category
      return _exhaustive
    }
  }
}

/**
 * SEO meta description and JSON-LD `description`: unique per place, front-loads name + geography + type,
 * then tagline or a plain-text excerpt from the long description. Length-limited for SERP display.
 */
export function buildPlaceDescription(place: PlaceForJsonLd): string {
  const name = place.name?.trim() || 'Place'
  const location = place.location?.trim()
  const kind = categoryPhraseForSentence(place.category)
  const tagLine = place.tagLine?.trim()
  const bodyPlain = plainTextFromRichContent(place.description ?? undefined)

  const geo = location ? geoPhraseForLocationDescription(location, place.locationExact) : ''
  const base = geo ? `${name} is a ${kind} ${geo}.` : `${name} is a ${kind}.`

  const detail = tagLine || bodyPlain
  const out = detail ? `${base} ${detail}` : base

  if (out.length <= SEO_META_DESCRIPTION_MAX_CHARS) return out

  if (detail) {
    const separatorLen = 1
    const budget = SEO_META_DESCRIPTION_MAX_CHARS - base.length - separatorLen
    if (budget >= 40) {
      const truncatedDetail = truncateMetaDescription(detail, budget)
      return `${base} ${truncatedDetail}`
    }
  }

  return truncateMetaDescription(out, SEO_META_DESCRIPTION_MAX_CHARS)
}

/**
 * BreadcrumbList aligned with `PageBreadcrumb` on the place detail route:
 * Home → Categories → Discover Places → current place.
 */
function buildPlaceBreadcrumbListJsonLd(placeName: string, placePageUrl: string): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: absoluteUrlForPath('/') },
      { '@type': 'ListItem', position: 2, name: 'Place Categories', item: absoluteUrlForPath('/place') },
      { '@type': 'ListItem', position: 3, name: 'Discover Places', item: absoluteUrlForPath('/place/list') },
      { '@type': 'ListItem', position: 4, name: placeName, item: placePageUrl },
    ],
  }
}

/** schema.org Person for the place listing owner (Profile). */
function buildPlaceOwnerAuthorJsonLd(owner: NonNullable<PlaceForJsonLd['owner']>): Record<string, unknown> {
  const profileUrl = absoluteUrlForPath(`/auth/signup`)
  const imageUrl = owner.profileMedia?.[0]?.imageUrlMedium ?? owner.coverMediaUrlLarge
  const name = owner.name?.trim() || 'Place owner'

  return {
    '@type': 'Person',
    name,
    url: profileUrl,
    ...(imageUrl ? { image: imageUrl } : {}),
  }
}

export function buildPlacePageJsonLd(input: {
  place: PlaceForJsonLd
  reviewsConnection: ReviewsConnection | null | undefined
  description: string
}): Record<string, unknown>[] {
  const { place, reviewsConnection, description: placeDescription } = input

  const pagePath = `/place/${place.slug ?? place.id}`
  const url = `${baseUrl()}${pagePath}`
  const entityId = `${url}#place`

  let reviewValue = 0
  if (place.reviewCount > 0) {
    reviewValue = Math.min(Math.max(0, place.reviewSum / place.reviewCount), 5.0)
  }

  const jsonLdReviews =
    reviewsConnection?.entries
      ?.filter((entry) => !entry.markedDeleted && entry.rating != null)
      .map((entry) => {
        const body = entry.content?.trim()
        return {
          '@type': 'Review',
          author: {
            '@type': 'Person',
            name: entry.owner?.name ?? 'Anonymous',
          },
          datePublished: entry.createdAt,
          reviewRating: {
            '@type': 'Rating',
            ratingValue: String(entry.rating),
          },
          ...(body ? { reviewBody: body } : {}),
        }
      }) ?? []

  const primaryType = schemaOrgTypeForPlaceCategory(place.category)
  const trimmedDescription = placeDescription.trim()
  const authorFromOwner = place.owner ? buildPlaceOwnerAuthorJsonLd(place.owner) : undefined

  const mainEntity: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@id': entityId,
    '@type': primaryType,
    name: place.name,
    ...(authorFromOwner && { author: authorFromOwner }),
    ...(trimmedDescription ? { description: trimmedDescription } : {}),
    url,
    category: schemaCategoryLabel(place.category),
    ...(place.phone && { telephone: place.phone }),
    ...(place.location && {
      address: {
        '@type': 'PostalAddress',
        addressLocality: place.location,
      },
    }),
    ...(place.locationExact &&
      place.locationLat != null &&
      place.locationLong != null && {
        geo: {
          '@type': 'GeoCoordinates',
          latitude: place.locationLat,
          longitude: place.locationLong,
        },
      }),
    ...(place.attachedMedia &&
      place.attachedMedia.length > 0 && {
        image: place.attachedMedia.slice(0, 5).map((media) => media.imageUrlMedium),
      }),
    ...(reviewValue > 0 && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: reviewValue.toFixed(1),
        reviewCount: place.reviewCount,
        bestRating: '5',
        worstRating: '1',
      },
    }),
    ...(jsonLdReviews.length > 0 && { review: jsonLdReviews }),
    ...(place.tags &&
      place.tags.length > 0 && {
        keywords: place.tags.map((tag) => tag.name).join(', '),
      }),
  }

  const relatedLink = relatedLinkFromWebLinks(place.webLinks)
  if (relatedLink) {
    mainEntity.relatedLink = relatedLink.length === 1 ? relatedLink[0] : relatedLink
  }

  const breadcrumbList = buildPlaceBreadcrumbListJsonLd(place.name.trim() || 'Place', url)

  return [mainEntity, breadcrumbList]
}

/** Loader fields used for `<head>` SEO on the place detail route. */
export type PlacePageSeoLoaderInput = {
  place?: PlaceQuery['place'] | null
  reviews?: ReviewsQuery['reviews'] | null
}

export function placePageHeadFromLoaderData(loaderData: PlacePageSeoLoaderInput | null | undefined) {
  const place = loaderData?.place ?? undefined
  const reviewsConnection = loaderData?.reviews

  if (!place) {
    return seo({ title: `Place | ${env.VITE_APP_NAME}` })
  }

  const placeDescription = buildPlaceDescription(place)
  const jsonLdData = buildPlacePageJsonLd({
    place,
    reviewsConnection,
    description: placeDescription,
  })

  const placeTitleName = place.name?.trim() || 'Place'
  const categoryTitle = categorySegmentForSeoTitle(place.category)

  return seo({
    title: `${placeTitleName} | ${categoryTitle} | ${env.VITE_APP_NAME}`,
    description: placeDescription,
    author: place?.owner?.name,
    url: `${baseUrl()}/place/${place.slug ?? place.id}`,
    image: place.attachedMedia?.[0]?.imageUrlMedium,
    robots: robotsMetaForEnvironment({
      production: place.supportersOnly ? 'noindex, nofollow' : 'index, follow',
      staging: 'noindex,nofollow',
    }),
    jsonLd: jsonLdData,
  })
}
